import { componentProvider } from 'js-component-framework';

/**
 * Control collapsing and expanding collapsable content.
 *
 * @param  {HTMLElement} options.element  The collapsable content's wrapper.
 * @param  {array}       options.children Array of child elements defined by the config.
 * @param  {object}      options.options  Arbritrary object of options.
 */
function collapsableController({ element, children, options }) {
    const { button, target } = children;
    const { collapsed } = options;

    if (null == target || null == button) {
        return;
    }

    let isUpdating = false;
    let resizeTimeout;

    // Function to calculate and set the max height
    const setMaxHeight = () => {
        if (isUpdating) return;
        isUpdating = true;

        // Temporarily remove collapsed class to get true height
        const wasCollapsed = target.classList.contains(collapsed);
        if (wasCollapsed) {
            target.classList.remove(collapsed);
        }
        
        // Force reflow to ensure accurate measurement
        target.offsetHeight;
        
        const boundingClientRect = target.getBoundingClientRect();
        const height = Math.max(boundingClientRect.height, target.scrollHeight);
        element.style.setProperty("--collapsable-content-max-height", `${height}px`);
        
        // Restore collapsed state if it was collapsed
        if (wasCollapsed) {
            target.classList.add(collapsed);
        }

        isUpdating = false;
    };

    // Debounced version for resize events
    const debouncedSetMaxHeight = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(setMaxHeight, 150);
    };

    // Initial height calculation with delay to ensure DOM is ready
    setTimeout(setMaxHeight, 0);

    // Recalculate on resize (important for mobile orientation changes)
    const resizeObserver = new ResizeObserver(debouncedSetMaxHeight);
    resizeObserver.observe(target);

    // Track state.
    const buttonIsExpanded = () => ('true' === button.getAttribute('aria-expanded'));

    // Allow to force expanded, e.g., the first item.
    if (!button.hasAttribute('aria-expanded')) {
        button.setAttribute('aria-expanded', 'false');
    }

    if (!buttonIsExpanded()) {
        target.classList.add(collapsed);
    }

    /**
     * Updates selector attributes on controller click.
     *
     * @param {Event} event The Event object.
     */
    const handleButtonClick = (event) => {
        event.currentTarget.setAttribute('aria-expanded', !buttonIsExpanded());
        target.classList.toggle(collapsed);
    }
    button.addEventListener('click', handleButtonClick);

    // Default the FAQ's to closed.
    button.setAttribute('aria-expanded', 'false');
    target.classList.add(collapsed);

    // Cleanup function
    return () => {
        clearTimeout(resizeTimeout);
        resizeObserver.disconnect();
        button.removeEventListener('click', handleButtonClick);
    };
}

const collapsableConfig = {
    name: 'collapsable',
    component: collapsableController,
    querySelector: {
        button: 'button',
        target: '.collapsable__content',
    },
    options: {
        collapsed: 'collapsable__content--collapsed',
    },
};

componentProvider(collapsableConfig);
